import Vue, { h } from 'vue';
import VueRouter from 'vue-router';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import AngularWrapper from '@/modules/angularDelegator/components/AngularWrapper.vue';
import notificationsRoutes from '@/modules/notificationCenter/routes';
import callsTextingRoutes from '@/modules/callsTexting/routes';
import textingRoutes from '@/modules/texting/routes';
import rolesAndPermissionsRoutes from '@/modules/rolesAndPermissions/routes';
import crmRoutes from '@/modules/crm/routes';
import paymentsRoutes from '@/modules/payments/routes';
import paymentSettingsRoutes from '@/modules/paymentSettings/routes';
import resourcesSettingsRoutes from '@/modules/resources/routes';
import bizAiSettingsRoutes from '@/modules/bizai/settings/routes';
import payGateProxyRoutes from '@/modules/payGateProxy/routes';
import reserveWithGoogleRoutes from '@/modules/reserveWithGoogle/routes';
import onlinePresenceRoutes from '@/modules/onlinePresence/routes';
import webAIWidgetRoutes from '@/modules/webAIWidget/routes';
import dashboardRoutes from '@/modules/dashboard/routes';
import messagingRoutes from '@/modules/messaging/routes';
import embeddedApps from '@/modules/app/routes';
import reportsRoutes from '@/modules/reports/routes';
import platformNavigationRoutes from '@/modules/layout/platformNavigation/routes';
import reviewsRoutes from '@/modules/reviews/routes';
import staffRoutes from '@/modules/staff/routes';
import accountRoutes from '@/modules/account/routes';
import pulseRoutes from '@/modules/pulse/routes';
import store from '@/store';
// import { PERMISSION_KEYS } from '@/modules/staff/utils/permissionKeysConsts';
import notificationSettingsRoutes from '@/modules/notifications/routes';
import appMarketRoutes from '@/modules/appMarket/routes';
import userErrorPageRoutes from '@/modules/userErrorPage/routes';

import { getValueByPatternMatch } from '@/utils/pathPatternMatcher';
import { markWebVitalsReady, resetWebVitalsReady } from '@/plugins/webVitals';

Vue.use(VueRouter);

const routes = [
  ...notificationsRoutes,
  ...crmRoutes,
  ...rolesAndPermissionsRoutes,
  ...paymentsRoutes,
  ...paymentSettingsRoutes,
  ...resourcesSettingsRoutes,
  ...bizAiSettingsRoutes,
  ...payGateProxyRoutes,
  ...reserveWithGoogleRoutes,
  ...onlinePresenceRoutes,
  ...webAIWidgetRoutes,
  ...dashboardRoutes,
  ...messagingRoutes,
  ...embeddedApps,
  ...reportsRoutes,
  ...platformNavigationRoutes,
  ...reviewsRoutes,
  ...staffRoutes,
  ...accountRoutes,
  ...notificationSettingsRoutes,
  ...pulseRoutes,
  ...appMarketRoutes,
  ...userErrorPageRoutes,
  {
    // THROWAWAY — manual STT smoke-test page; remove with src/modules/sttPlayground.
    path: '/stt-playground',
    name: 'sttPlayground',
    component: () => import('@/modules/sttPlayground/views/SttPlaygroundView.vue'),
  },
  {
    path: '/app_market',
    component: { render: () => h('router-view') },
    children: [
      ...callsTextingRoutes,
      ...textingRoutes,
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'angular',
    component: AngularWrapper,
    meta: {
      isAngular: true,
      /**
      permissions: {
         * For Angular routes, we can set the permissions per route, here are some examples:
         'appointment-list': [PERMISSION_KEYS.key1, PERMISSION_KEYS.key2],
         'clients/$': PERMISSION_KEYS.key3,
         'settings/services/$': [PERMISSION_KEYS.key4, PERMISSION_KEYS.key5],
      },
       */
    },
  },
];

const router = new VueRouter({
  mode: 'history',
  base: '/app',
  routes,
});

// Identify when the user clicks the back button
let previousRoute = null;
let popStateDetected = false;
window.addEventListener('popstate', () => {
  popStateDetected = true;
});

// For a popstate onto a state-only entry (same URL): vue-router aborts it as a duplicate before
// beforeEach runs, so the flag would stay set and the next navigation would be taken for a Back.
router.consumePopState = () => {
  popStateDetected = false;
};

router.handleBack = ({ replace = false } = {}) => {
  if (previousRoute?.name) {
    if (replace) {
      router.replace(previousRoute.fullPath);
    } else {
      router.go(-1);
    }
  } else {
    router.push({ name: 'dashboard' });
  }
};

const handleLocalization = (i18n) => {
  Vue.prototype.clearCurrentI18nModules();

  if (i18n) {
    let modulesToLoad = i18n;
    if (typeof i18n === 'string') {
      modulesToLoad = [i18n];
    }

    modulesToLoad.forEach((module) => {
      Vue.prototype.addI18nModule(module);
    });
  }
};
const handlePermissions = async (toPathPermissions) => {
  if (!toPathPermissions) {
    return true;
  }
  const permissions = [];
  if (toPathPermissions) {
    if (typeof toPathPermissions === 'string') {
      permissions.push(toPathPermissions);
    } else {
      toPathPermissions.map((item) => permissions.push(item));
    }
  }
  const allPermissions = await Vue.prototype.$hasPermissionsAsync(permissions);

  return Object.values(allPermissions).every((item) => !!item);
};

const handleAngularPermissions = async (path, allPathPermissions = {}) => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  if (cleanPath.length === 0) {
    return true;
  }
  const pathPermissions = getValueByPatternMatch(cleanPath, allPathPermissions);
  if (!pathPermissions) {
    return true;
  }

  return handlePermissions(pathPermissions);
};

router.beforeEach(async (to, from, next) => {
  // Reset web vitals ready state on every navigation
  resetWebVitalsReady();

  const hasPermission = await (to.meta.isAngular ? handleAngularPermissions(to.path, to.meta.permissions) : handlePermissions(to.meta.permissions));

  if (!hasPermission) {
    next({ name: 'userError', params: { errorType: 'unauthorised' } });
  }

  store.dispatch('setOriginPage', from.path);

  // Whenever the current page is a pov page, put Angular into modal mode
  // and vice versa. Skipped on a query-only change: the page is the same, and re-hiding
  // would undo a `navigate-to` that PovgularWrapper just sent to show Angular.
  if (to.path !== from.path || !from.matched.length) {
    store.dispatch('ModalStore/showAngularBackground', !!to.meta.isAngular);
  }

  previousRoute = from;
  const isItABackButton = popStateDetected;
  popStateDetected = false;

  const isAngularDisplayed = store.getters['AngularStore/isAngularDisplayed'];
  const isGoingToAngularPage = !!to.meta.isAngular;

  // Case of going back to Angular page from POV page
  if (isItABackButton && !isAngularDisplayed && isGoingToAngularPage) {
    outgoingPostMessageUtils.post('get-top-bar-config');
  }

  if (isItABackButton && isAngularDisplayed && isGoingToAngularPage && to.path !== from.path) {
    // Ask the iframe to go back
    outgoingPostMessageUtils.post('navigation-back');
  }

  handleLocalization(to?.meta?.i18n);

  next();
});

router.afterEach((to, from) => {
  const pageNameEn = to.meta?.pageNameEn;
  if (pageNameEn) {
    // Flush queued Web Vitals now that page title is set
    markWebVitalsReady(pageNameEn);
    Vue.prototype.$trackPageName(pageNameEn);
  }

  const isGoingToAngularPage = !!to.meta?.isAngular;
  const wasOnAngularPage = !!from?.meta?.isAngular;
  if (isGoingToAngularPage && !wasOnAngularPage) {
    outgoingPostMessageUtils.post('get-top-bar-config');
  }
});

export default router;
